import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Check as CheckIcon, Copy, ExternalLink, Crown, Users, Zap, Ticket } from "lucide-react";

const TIERS = [
  {
    id: "free", name: "FREE", price: "$0", icon: Zap,
    tagline: "Get in the door",
    features: [
      "Basic habit tracker (3 habits)",
      "10 tasks per week",
      "One pipeline (Deals)",
      "Basic dashboard",
      "Light / Dark mode",
      "4 weeks of history",
    ],
  },
  {
    id: "pro", name: "PRO", price: "$10", icon: Crown,
    tagline: "The full operating system",
    features: [
      "Unlimited habits, tasks & pipelines",
      "Drag-and-drop task management",
      "Priority sorting + time scheduling",
      "Full dashboard, streaks & analytics",
      "Google Calendar integration",
      "AI schedule generation & daily brief",
      "Full history + future planning",
      "All future Pro features",
    ],
  },
  {
    id: "team", name: "TEAM", price: "$20", icon: Users,
    tagline: "Run the whole operation",
    features: [
      "Everything in Pro",
      "Team workspaces & shared pipelines",
      "Assign tasks to team members",
      "Shared calendars",
      "Team analytics & dashboard",
      "Role-based permissions",
      "Team activity feed",
      "All future Team features",
    ],
  },
];

export default function Billing() {
  const { user, refresh } = useAuth();
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(null); // plan id being processed
  const [msg, setMsg] = useState(null);
  const [params] = useSearchParams();

  const load = async () => {
    const { data } = await api.get("/billing/me");
    setMe(data);
  };

  useEffect(() => {
    load();
    const sid = params.get("session_id");
    if (sid) {
      // Poll checkout result a few times, then refresh state.
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const { data } = await api.get(`/billing/status/${sid}`);
          if (data.payment_status === "paid") {
            setMsg({ ok: true, text: "Subscription active — welcome aboard." });
            await refresh();
            await load();
            return;
          }
        } catch (_) {}
        if (tries < 6) setTimeout(poll, 2000);
      };
      poll();
    }
    if (params.get("cancelled")) {
      setMsg({ ok: false, text: "Checkout cancelled — no charge made." });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const plan = user?.plan || "free";

  const choose = async (tier) => {
    if (tier === plan) return;
    setMsg(null);
    if (tier === "free") {
      // Downgrade to free = cancel via portal
      return openPortal();
    }
    setBusy(tier);
    try {
      const { data } = await api.post("/billing/subscribe", {
        plan: tier, origin_url: window.location.origin,
      });
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      if (data.switched) {
        setMsg({ ok: true, text: `Plan switched to ${tier.toUpperCase()} — prorated on your next invoice.` });
        await refresh();
        await load();
      } else if (data.detail) {
        setMsg({ ok: true, text: data.detail });
      }
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Could not start checkout" });
    } finally {
      setBusy(null);
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Could not open portal" });
      setBusy(null);
    }
  };


  return (
    <div className="space-y-8" data-testid="billing-page">
      <header>
        <div className="kpi-label">Account</div>
        <h1 className="display text-3xl sm:text-5xl mt-3 leading-none font-bold uppercase tracking-tight">
          Plans &amp; Billing
        </h1>
        <div className="mono text-[11px] uppercase tracking-widest text-muted-foreground mt-3">
          Current plan: <span className="text-oud">{plan.toUpperCase()}</span>
          {user?.subscription_status === "past_due" && (
            <span className="text-[hsl(var(--oxblood))] ml-3">· PAYMENT PAST DUE</span>
          )}
        </div>
      </header>

      {msg && (
        <div
          className={`hud-frame p-4 mono text-[11px] uppercase tracking-widest ${msg.ok ? "text-oud" : "text-[hsl(var(--oxblood))]"}`}
          data-testid="billing-msg"
        >
          {msg.text}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
        {TIERS.map((t) => {
          const Icon = t.icon;
          const current = t.id === plan;
          const isUpgrade = TIERS.findIndex((x) => x.id === t.id) > TIERS.findIndex((x) => x.id === plan);
          return (
            <div
              key={t.id}
              className={`hud-frame p-6 flex flex-col ${t.id === "pro" ? "border-oud/70" : ""}`}
              style={current ? { borderColor: "hsl(var(--oxblood))" } : null}
              data-testid={`tier-${t.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="kpi-label flex items-center gap-2"><Icon size={11} /> {t.name}</div>
                {t.id === "pro" && !current && (
                  <span className="mono text-[9px] uppercase tracking-widest text-oud border border-oud/50 px-1.5 py-0.5">Most popular</span>
                )}
                {current && (
                  <span className="mono text-[9px] uppercase tracking-widest text-[hsl(var(--oxblood))] border border-[hsl(var(--oxblood))] px-1.5 py-0.5">Current</span>
                )}
              </div>
              <div className="flex items-baseline gap-2 mt-4">
                <div className="display text-4xl font-bold">{t.price}</div>
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">/ MO</div>
              </div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{t.tagline}</div>
              <div className="hud-divider my-4" />
              <ul className="space-y-2 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <CheckIcon size={12} className="text-hud mt-1 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => choose(t.id)}
                disabled={current || busy !== null}
                className={`mt-6 w-full justify-center ${current ? "hud-btn opacity-50" : isUpgrade ? "hud-btn hud-btn-primary" : "hud-btn"}`}
                data-testid={`tier-${t.id}-btn`}
              >
                {current ? "CURRENT PLAN"
                  : busy === t.id ? "..."
                  : t.id === "free" ? "DOWNGRADE"
                  : isUpgrade ? `UPGRADE TO ${t.name}` : `SWITCH TO ${t.name}`}
              </button>
            </div>
          );
        })}
      </div>

      {user?.stripe_customer_id && (
        <button onClick={openPortal} disabled={busy !== null} className="hud-btn flex items-center gap-2" data-testid="billing-portal-btn">
          <ExternalLink size={11} /> Open Customer Portal — manage card, invoices, cancel
        </button>
      )}

      <RedeemCode onRedeemed={async () => { await refresh(); await load(); }} setMsg={setMsg} />

      <ReferralDashboard user={user} />
    </div>
  );
}

function RedeemCode({ onRedeemed, setMsg }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const c = code.trim();
    if (!c) return;
    setBusy(true);
    try {
      const { data } = await api.post("/billing/redeem", { code: c });
      setMsg({ ok: true, text: data.message || "Code redeemed." });
      setCode("");
      await onRedeemed();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Could not redeem code" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="hud-frame p-6 space-y-4" data-testid="billing-redeem">
      <div className="kpi-label flex items-center gap-2">
        <Ticket size={11} /> Promo / Comp Code
      </div>
      <p className="text-sm text-muted-foreground">
        Got a comp code? Redeem it here to unlock lifetime access. VIP grants and
        owner comps run through this door.
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="ENTER CODE"
          className="hud-input flex-1 mono tracking-[0.25em]"
          data-testid="redeem-code-input"
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
        />
        <button
          onClick={submit}
          disabled={busy || !code.trim()}
          className="hud-btn hud-btn-primary sm:w-auto"
          data-testid="redeem-code-btn"
        >
          {busy ? "REDEEMING…" : "REDEEM"}
        </button>
      </div>
    </section>
  );
}

function ReferralDashboard({ user }) {
  const [d, setD] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/referrals/dashboard").then((r) => setD(r.data)).catch(() => {});
  }, []);

  const copyLink = () => {
    navigator.clipboard.writeText(d?.referral_link || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const progress = d?.progress_to_next_reward ?? 0;

  return (
    <section className="hud-frame p-6 space-y-5" data-testid="billing-referrals">
      <div className="kpi-label">Referral Program</div>
      <p className="text-sm text-muted-foreground">
        Refer <span className="text-oud">two paying subscribers</span> and earn a full year of your
        plan, free. Repeatable — every two paying sign-ups adds another year. Billing resumes
        automatically when a free year ends.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "PENDING", value: d?.pending_count ?? "—", tid: "ref-pending" },
          { label: "SUCCESSFUL", value: d?.successful_count ?? "—", tid: "ref-successful" },
          { label: "FREE YEARS EARNED", value: d?.free_years_earned ?? "—", tid: "ref-years" },
          { label: "REWARD TIME LEFT", value: d?.reward_active ? `${d.reward_days_left}d` : "—", tid: "ref-days-left" },
        ].map((s) => (
          <div key={s.label} className="border border-border p-4" data-testid={s.tid}>
            <div className="kpi-label">{s.label}</div>
            <div className="display text-3xl mt-2">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="border border-dashed border-border p-4 flex items-center justify-between gap-4" data-testid="ref-progress">
        <div className="mono text-[11px] uppercase tracking-widest text-muted-foreground">
          Progress to next free year
        </div>
        <div className="flex items-center gap-2">
          {[0, 1].map((i) => (
            <span
              key={i}
              className={`w-8 h-2 ${i < progress ? "bg-oud" : "bg-[hsl(var(--surface))] border border-border"}`}
            />
          ))}
          <span className="mono text-[11px] text-oud ml-2">{progress}/2</span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <code className="mono text-xs bg-[hsl(var(--surface))] border border-border px-3 py-2 truncate max-w-full" data-testid="referral-link">
          {d?.referral_link || "…"}
        </code>
        <button onClick={copyLink} className="hud-btn" data-testid="copy-referral">
          <Copy size={11} /> {copied ? "COPIED" : "COPY"}
        </button>
        <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Code: <span className="text-foreground">{d?.referral_code || user?.referral_code}</span>
        </span>
      </div>

      {(d?.pending?.length > 0 || d?.successful?.length > 0) && (
        <div className="space-y-1">
          {d.successful.map((r, i) => (
            <div key={`s${i}`} className="flex items-center justify-between mono text-[11px] uppercase tracking-widest">
              <span className="truncate">{r.email}</span>
              <span className="text-oud">PAYING ✓</span>
            </div>
          ))}
          {d.pending.map((r, i) => (
            <div key={`p${i}`} className="flex items-center justify-between mono text-[11px] uppercase tracking-widest text-muted-foreground">
              <span className="truncate">{r.email}</span>
              <span>SIGNED UP · NOT PAYING YET</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
